import React, { useState, useEffect } from 'react'
import { Box, Typography, Fade, Slide, LinearProgress, useTheme } from '@mui/material'


const PRO_TIPS = [
    "Instead of simply re-reading your notes, use these practice sessions to force your brain to retrieve information—this 'active recall' is the most efficient way to build long-term memory.",
    "Always read the rationale provided after an answer; understanding why a distractor was incorrect is just as valuable as knowing the correct answer.",
    "Consistent, shorter practice sessions are scientifically proven to be more effective than 'cramming' for several hours once a week.",
    "Your daily streak is more than just a number; it represents the neural pathways you are strengthening through daily repetition.",
    "Use your practice history to identify which modules have the lowest scores and prioritize those for your next intensive study session.",
    "Treat every incorrect answer as a high-value data point; it highlights exactly where your mental model of the subject needs refinement.",
    "Try explaining the concept behind a difficult question to a peer; the act of teaching is one of the highest forms of cognitive mastery.",
    "Do not be discouraged by low initial scores in a new module; complex subjects require multiple layers of engagement to master.",
]

const TipsAndMessages = ( { variant = 'slide', interval = 5000 } ) => {
    const [currentTipIndex, setCurrentTipIndex] = useState( 0 )
    const [showTip, setShowTip] = useState( true )
    const theme = useTheme()

    useEffect( () => {
        const timer = setInterval( () => {
            setShowTip( false ) // Start fade/slide out
            setTimeout( () => {
                setCurrentTipIndex( ( prevIndex ) => ( prevIndex + 1 ) % PRO_TIPS.length )
                setShowTip( true ) // Start fade/slide in
            }, 500 ) // This duration should match your animation speed
        }, interval )

        return () => clearInterval( timer )
    }, [interval] )

    const commonProps = {
        padding: '15px 20px',
        borderRadius: theme.shape.borderRadius,
        bgcolor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText,
        minHeight: '100px', // Ensure consistent height to prevent layout shifts
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        position: 'relative', // Needed for absolute positioning of the progress bar
        overflow: 'hidden', // Hide overflow during slide transitions
        boxShadow: theme.shadows[3],
    }

    const messageContent = (
        <Box display="flex" alignItems="flex-start" flexDirection="column">
            <Typography variant="subtitle2" component="p" sx={{ fontWeight: 'bold', mb: 1 }}>
                Pro-Tip:
            </Typography>
            <Typography textAlign="left" variant="body2" component="p" fontSize="13px" lineHeight="1.5">
                {PRO_TIPS[currentTipIndex]}
            </Typography>
            {/* Optional: Add a subtle linear progress bar at the bottom */}
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
                <LinearProgress
                    variant="indeterminate"
                    color="secondary" // Or "primary"
                    sx={{ height: 3, opacity: 0.6 }}
                />
            </Box>
        </Box>
    )

    return (
        <Box sx={{ maxWidth: '450px' }}>
            {variant === 'fade' ? (
                <Fade in={showTip} timeout={500}>
                    <Box {...commonProps}>
                        {messageContent}
                    </Box>
                </Fade>
            ) : ( // Default to slide
                <Slide in={showTip} direction="up" timeout={500} mountOnEnter unmountOnExit>
                    <Box {...commonProps}>
                        {messageContent}
                    </Box>
                </Slide>
            )}
        </Box>
    )
}

export default TipsAndMessages